import {
  CONSENT_VERSION,
  CONTACT_EMAIL,
  PROMPT_KEYS,
  PROMPTS,
  STORY_TEXT_MAX,
  STORY_TEXT_MIN,
  STORY_TITLE_MAX,
  SUBMISSION_KINDS,
  type IdentityPreference,
  type PromptKey,
  type SubmissionCreated,
  type SubmissionKind,
} from "@kmstories/shared";
import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { AudioRecorder } from "../components/AudioRecorder.js";
import { Ltr } from "../components/Bdi.js";
import { ConsentBlock, EMPTY_CONSENTS, type ConsentState } from "../components/ConsentBlock.js";
import { useToast } from "../components/Toast.js";
import { VideoUploader } from "../components/VideoUploader.js";
import { useT } from "../i18n/LocaleProvider.js";
import { api, ApiError } from "../lib/api.js";
import { useDocumentTitle } from "../lib/useDocumentTitle.js";
import { useFormToken, type FormId } from "../lib/useFormToken.js";

/**
 * The 3-step story wizard. Everything typed is mirrored to sessionStorage on
 * every keystroke, so no error, reload or upload hiccup ever costs someone the
 * story they were writing. The trust panel sits ABOVE the form on purpose:
 * trust is built before the first field, not in the fine print after it.
 */
interface Draft {
  step: 1 | 2 | 3;
  kind: SubmissionKind | null;
  storyTitle: string;
  storyText: string;
  note: string;
  linkUrl: string;
  promptKey: PromptKey | null;
  mediaId: string | null;
  identityPreference: IdentityPreference;
  displayName: string;
  email: string;
  consents: ConsentState;
}

const DRAFT_KEY = "kms.story.draft.v1";
const emptyDraft: Draft = {
  step: 1,
  kind: null,
  storyTitle: "",
  storyText: "",
  note: "",
  linkUrl: "",
  promptKey: null,
  mediaId: null,
  identityPreference: "anonymous",
  displayName: "",
  email: "",
  consents: EMPTY_CONSENTS,
};

function loadDraft(): Draft {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return emptyDraft;
    return { ...emptyDraft, ...(JSON.parse(raw) as Partial<Draft>) };
  } catch {
    return emptyDraft;
  }
}

export function SharePage() {
  const { m, lp, locale } = useT();
  useDocumentTitle(m.meta.pages.share);
  const [params] = useSearchParams();
  const [d, setD] = useState<Draft>(() => {
    const draft = loadDraft();
    const p = params.get("prompt");
    if (p && (PROMPT_KEYS as readonly string[]).includes(p) && !draft.storyText) {
      return { ...draft, promptKey: p as PromptKey, kind: draft.kind ?? "text", step: draft.kind ? draft.step : 2 };
    }
    return draft;
  });
  const [done, setDone] = useState<SubmissionCreated | null>(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showConsentError, setShowConsentError] = useState(false);
  const { getToken } = useFormToken("story");
  const { getToken: getUploadToken } = useFormToken("upload");
  const headingRef = useRef<HTMLHeadingElement>(null);
  const toast = useToast();

  useEffect(() => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(d));
    } catch {
      // private mode
    }
  }, [d]);

  // Focus management between steps: the step heading receives focus so
  // keyboard and screen-reader users land at the top of the new step.
  useEffect(() => {
    headingRef.current?.focus();
  }, [d.step]);

  const patch = useCallback((p: Partial<Draft>) => setD((cur) => ({ ...cur, ...p })), []);
  const tokenFor = useCallback((formId: FormId) => (formId === "upload" ? getUploadToken() : getToken()), [getToken, getUploadToken]);

  function validateStep2(): boolean {
    const e: Record<string, string> = {};
    if (d.kind === "text" && d.storyText.trim().length < STORY_TEXT_MIN) e.storyText = m.share.errors.storyRequired;
    if (d.kind === "link" && !/^https:\/\/\S+/.test(d.linkUrl.trim())) e.linkUrl = m.share.errors.linkRequired;
    if ((d.kind === "audio" || d.kind === "video") && !d.mediaId) e.mediaId = m.share.errors.mediaRequired;
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (d.identityPreference !== "anonymous" && !d.displayName.trim()) errs.displayName = m.share.errors.nameRequired;
    const needsEmail = d.consents.futureContact || d.consents.updates;
    if (needsEmail && !d.email.trim()) errs.email = m.share.errors.emailRequired;
    if (d.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim())) errs.email = m.share.errors.emailInvalid;
    const consentOk = d.consents.storageReview && d.consents.ageConfirmed;
    setShowConsentError(!consentOk);
    setErrors(errs);
    if (Object.keys(errs).length || !consentOk) return;

    // React clears currentTarget after the synchronous dispatch — read the form before any await.
    const form = e.currentTarget as HTMLFormElement;
    setBusy(true);
    try {
      const formToken = await getToken();
      const hp = form.elements.namedItem("fax") as HTMLInputElement | null;
      const body = {
        kind: d.kind,
        storyTitle: d.storyTitle.trim() || undefined,
        storyText: d.kind === "text" ? d.storyText.trim() : undefined,
        note: d.kind !== "text" && d.note.trim() ? d.note.trim() : undefined,
        linkUrl: d.kind === "link" ? d.linkUrl.trim() : undefined,
        mediaId: d.kind === "audio" || d.kind === "video" ? d.mediaId ?? undefined : undefined,
        promptKey: d.promptKey ?? undefined,
        identityPreference: d.identityPreference,
        displayName: d.identityPreference === "anonymous" ? undefined : d.displayName.trim(),
        email: d.email.trim() || undefined,
        locale,
        consentVersion: CONSENT_VERSION,
        consents: { ...d.consents, storageReview: true, ageConfirmed: true },
        formToken,
        fax: hp?.value ?? "",
      };
      const created = await api.post<SubmissionCreated>("/api/submissions", body);
      setDone(created);
      try {
        sessionStorage.removeItem(DRAFT_KEY);
      } catch {
        // fine
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "form_token_too_fast") setErrors({ form: m.share.errors.tooFast });
        else if (err.status === 429) setErrors({ form: m.share.errors.rateLimited });
        else if (err.status === 400 && err.issues.length) {
          const mapped: Record<string, string> = { form: m.share.errors.invalid };
          for (const i of err.issues) {
            if (i.path.startsWith("email")) mapped.email = m.share.errors.emailInvalid;
            if (i.path.startsWith("displayName")) mapped.displayName = m.share.errors.nameRequired;
            if (i.path.startsWith("storyText")) mapped.storyText = m.share.errors.storyRequired;
            if (i.path.startsWith("linkUrl")) mapped.linkUrl = m.share.errors.linkRequired;
            if (i.path.startsWith("mediaId")) mapped.mediaId = m.share.errors.mediaRequired;
          }
          setErrors(mapped);
        } else setErrors({ form: m.share.errors.generic });
      } else setErrors({ form: m.share.errors.generic });
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const link = `${origin}${lp(`/w/${done.withdrawToken}`)}`;
    return (
      <div className="container container--narrow section">
        <div className="done">
          <h1>{m.share.done.heading}</h1>
          <p className="hero__lede">{m.share.done.body}</p>
          <div className="card card--paper done__link">
            <h2 style={{ fontSize: "1.2rem", margin: 0 }}>{m.share.done.linkHeading}</h2>
            <p style={{ margin: 0 }}>{m.share.done.linkBody}</p>
            <div className="copybox">
              <code>{link}</code>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => navigator.clipboard.writeText(link).then(() => toast(m.share.done.copied)).catch(() => window.prompt(m.share.done.copy, link))}
              >
                {m.share.done.copy}
              </button>
            </div>
            {/* Both branches read the SERVER's verdict. Gating on d.email asks
                "did they give us an address", which is not the same question as
                "did the mail go", and answering the first while printing the
                second is what made this screen lie for two days. */}
            {done.receipt === "sent" && (
              <p className="muted small" style={{ margin: 0 }}>{m.share.done.emailed}</p>
            )}
            {done.receipt === "failed" && (
              <p className="notice" role="status" style={{ margin: 0 }}>{m.share.done.notEmailed}</p>
            )}
          </div>
          <p className="muted">{m.share.done.expectation}</p>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              setDone(null);
              setD(emptyDraft);
            }}
          >
            {m.share.done.another}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container container--narrow">
      <div className="page-head">
        <h1>{m.share.heading}</h1>
        <p className="page-head__lede">{m.storyBlock.invitation}</p>
      </div>

      <TrustPanel />

      <div className="steps" aria-hidden="true">
        <span>{m.share.stepOf.replace("{n}", locale === "fa" ? ["۱", "۲", "۳"][d.step - 1]! : String(d.step))}</span>
        <div className="steps__bar">
          <div className="steps__fill" style={{ width: `${(d.step / 3) * 100}%` }} />
        </div>
      </div>
      <p className="visually-hidden" aria-live="polite">
        {m.share.stepOf.replace("{n}", String(d.step))}
      </p>

      {d.step === 1 && (
        <section className="form" aria-labelledby="step1">
          <h2 id="step1" ref={headingRef} tabIndex={-1}>
            {m.share.step1Heading}
          </h2>
          <div className="radio-cards" role="radiogroup" aria-labelledby="step1">
            {SUBMISSION_KINDS.map((k) => (
              <label key={k} className="radio-card">
                <input type="radio" name="kind" value={k} checked={d.kind === k} onChange={() => patch({ kind: k, mediaId: null })} />
                <span className="radio-card__title">{m.share.formats[k].title}</span>
                <p className="radio-card__body">{m.share.formats[k].body}</p>
              </label>
            ))}
          </div>
          <p className="muted small">{m.share.noAccount}</p>
          <div className="actions" style={{ justifyContent: "flex-end" }}>
            <button type="button" className="btn btn--primary" disabled={!d.kind} onClick={() => patch({ step: 2 })}>
              {m.share.next}
            </button>
          </div>
        </section>
      )}

      {d.step === 2 && d.kind && (
        <section className="form" aria-labelledby="step2">
          <h2 id="step2" ref={headingRef} tabIndex={-1}>
            {m.share.step2Heading}
          </h2>

          <div className="field">
            <span className="field__label">{m.storyBlock.promptsHeading}</span>
            <p className="field__hint">{m.share.promptsHint}</p>
            <div className="chips">
              {PROMPT_KEYS.map((k) => (
                <button key={k} type="button" className="chip" aria-pressed={d.promptKey === k} onClick={() => patch({ promptKey: d.promptKey === k ? null : k })}>
                  {PROMPTS[k][locale]}
                </button>
              ))}
            </div>
          </div>

          {d.kind === "text" && (
            <>
              <Field id="title" label={m.share.titleLabel}>
                <input id="title" className="input" dir="auto" maxLength={STORY_TITLE_MAX} value={d.storyTitle} onChange={(e) => patch({ storyTitle: e.target.value })} />
              </Field>
              <Field id="story" label={m.share.textLabel} error={errors.storyText} hint={m.share.charCount.replace("{count}", String(d.storyText.length)).replace("{max}", String(STORY_TEXT_MAX))}>
                <textarea
                  id="story"
                  className="textarea textarea--story"
                  dir="auto"
                  maxLength={STORY_TEXT_MAX}
                  placeholder={d.promptKey ? PROMPTS[d.promptKey][locale] : m.share.textPlaceholder}
                  value={d.storyText}
                  onChange={(e) => patch({ storyText: e.target.value })}
                  aria-invalid={errors.storyText ? true : undefined}
                />
              </Field>
            </>
          )}

          {d.kind === "audio" && (
            <div className="field">
              <span className="field__label">{m.share.formats.audio.title}</span>
              <AudioRecorder getToken={tokenFor} initial={d.mediaId ? { mediaId: d.mediaId } : null} onReady={(id) => patch({ mediaId: id })} onReset={() => patch({ mediaId: null })} />
              {errors.mediaId && <p className="field__error" role="alert">{errors.mediaId}</p>}
            </div>
          )}

          {d.kind === "video" && (
            <div className="field">
              <span className="field__label">{m.share.formats.video.title}</span>
              <VideoUploader getToken={tokenFor} initial={d.mediaId ? { mediaId: d.mediaId } : null} onReady={(id) => patch({ mediaId: id })} onReset={() => patch({ mediaId: null })} />
              {errors.mediaId && <p className="field__error" role="alert">{errors.mediaId}</p>}
            </div>
          )}

          {d.kind === "link" && (
            <Field id="link" label={m.share.linkLabel} error={errors.linkUrl} hint={m.share.linkHelp}>
              <input id="link" className="input" type="url" inputMode="url" dir="ltr" placeholder={m.share.linkPlaceholder} value={d.linkUrl} onChange={(e) => patch({ linkUrl: e.target.value })} aria-invalid={errors.linkUrl ? true : undefined} />
              <p className="notice small" style={{ marginBlockStart: "0.5rem" }}>
                {m.share.linkWarning}
              </p>
            </Field>
          )}

          {d.kind !== "text" && (
            <Field id="note" label={m.share.noteLabel}>
              <textarea id="note" className="textarea" style={{ minHeight: "6rem" }} dir="auto" maxLength={500} value={d.note} onChange={(e) => patch({ note: e.target.value })} />
            </Field>
          )}

          <div className="actions">
            <button type="button" className="btn btn--ghost" onClick={() => patch({ step: 1 })}>
              {m.share.back}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => {
                if (validateStep2()) patch({ step: 3 });
              }}
            >
              {m.share.next}
            </button>
          </div>
        </section>
      )}

      {d.step === 3 && (
        <form className="form" onSubmit={submit} noValidate aria-labelledby="step3">
          <h2 id="step3" ref={headingRef} tabIndex={-1}>
            {m.share.step3Heading}
          </h2>

          <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="field__label">{m.share.identityLabel}</legend>
            <div className="radio-inline">
              {(["real_name", "pseudonym", "anonymous"] as IdentityPreference[]).map((v) => (
                <label key={v}>
                  <input type="radio" name="identity" value={v} checked={d.identityPreference === v} onChange={() => patch({ identityPreference: v })} />
                  {m.share.identity[v]}
                </label>
              ))}
            </div>
            <p className="field__hint">{m.share.identityHint}</p>
          </fieldset>

          {d.identityPreference !== "anonymous" && (
            <Field id="name" label={d.identityPreference === "pseudonym" ? m.share.pseudonymLabel : m.share.nameLabel} error={errors.displayName}>
              <input id="name" className="input" dir="auto" autoComplete={d.identityPreference === "real_name" ? "name" : "off"} value={d.displayName} onChange={(e) => patch({ displayName: e.target.value })} aria-invalid={errors.displayName ? true : undefined} />
            </Field>
          )}

          <Field id="email" label={m.share.emailLabel} error={errors.email} hint={d.consents.futureContact || d.consents.updates ? m.share.emailRequiredHint : m.share.emailHint}>
            <input id="email" className="input" type="email" inputMode="email" autoComplete="email" dir="ltr" value={d.email} onChange={(e) => patch({ email: e.target.value })} aria-invalid={errors.email ? true : undefined} />
          </Field>

          <ConsentBlock value={d.consents} onChange={(c) => patch({ consents: c })} showError={showConsentError} />
          <p className="muted small">
            {m.share.under18.split(CONTACT_EMAIL)[0]}
            <Ltr>{CONTACT_EMAIL}</Ltr>
            {m.share.under18.split(CONTACT_EMAIL)[1]}
          </p>

          <div className="hp" aria-hidden="true">
            <label>
              Fax
              <input type="text" name="fax" tabIndex={-1} autoComplete="off" defaultValue="" />
            </label>
          </div>

          {errors.form && (
            <p className="field__error" role="alert">
              {errors.form}
            </p>
          )}
          <div className="actions">
            <button type="button" className="btn btn--ghost" onClick={() => patch({ step: 2 })}>
              {m.share.back}
            </button>
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy ? m.share.sending : m.share.submit}
            </button>
          </div>
        </form>
      )}

      <p className="muted small" style={{ marginBlockStart: "2rem" }}>
        {m.share.readBy} {m.share.expectation}
      </p>
    </div>
  );
}

function Field({ id, label, hint, error, children }: { id: string; label: string; hint?: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="field">
      <label htmlFor={id} className="field__label">
        {label}
      </label>
      {children}
      {error ? (
        <p className="field__error" role="alert">
          {error}
        </p>
      ) : (
        hint && <p className="field__hint">{hint}</p>
      )}
    </div>
  );
}

function TrustPanel() {
  const { m } = useT();
  const id = useId();
  const rows = [
    [m.share.trust.private, "🔒"],
    [m.share.trust.sweden, "🇸🇪"],
    [m.share.trust.identity, "👤"],
    [m.share.trust.withdraw, "↩"],
  ] as const;
  return (
    <section className="card" aria-labelledby={id} style={{ marginBlockEnd: "2rem" }}>
      <h2 id={id} style={{ fontSize: "1.2rem" }}>
        {m.share.trustHeading}
      </h2>
      <div className="trust">
        {rows.map(([text, icon]) => (
          <div key={text} className="trust__row">
            <span className="trust__icon" aria-hidden="true">
              {icon}
            </span>
            <p style={{ margin: 0 }}>{text}</p>
          </div>
        ))}
      </div>
      <details style={{ marginBlockStart: "1rem" }}>
        <summary className="linkish" style={{ fontSize: "1rem" }}>
          {m.share.commitmentsHeading}
        </summary>
        <ul style={{ marginBlockStart: "0.75rem", color: "var(--text-muted)" }}>
          <li>{m.share.commitments.contactFirst}</li>
          <li>{m.share.commitments.review}</li>
          <li>{m.share.commitments.blur}</li>
          <li>{m.share.commitments.redact}</li>
          <li>{m.share.commitments.metadata}</li>
        </ul>
      </details>
    </section>
  );
}
